import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, FileText, Pill as PillIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { Loading, StateBlock } from "@/components/common";
import { ActionButton, Card, CardMeta, CardTitle, PageHero, Section, StatusBadge } from "./PageShell";
import { PushPreferenceCard } from "@/components/PushPreferenceCard";
import { useSelectedPatient } from "./patientContext";

const AlertCard = ({ alert, onOpen, onMarkRead, index }) => (
  <Card
    testId={`apoio-alert-${alert.id}`}
    tone={alert.read ? "default" : "highlight"}
    onClick={onOpen ? () => onOpen(alert) : undefined}
    role={onOpen ? "link" : undefined}
    tabIndex={onOpen ? 0 : undefined}
    className={`er-rise ${onOpen ? "cursor-pointer" : ""}`}
    style={{ animationDelay: `${index * 60}ms` }}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3.5">
        <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sage-50 text-sage-700">
          {alert.type === "bulletin" ? (
            <FileText size={17} aria-hidden="true" />
          ) : alert.type === "medication_event" ? (
            <PillIcon size={17} aria-hidden="true" />
          ) : (
            <ShieldCheck size={17} aria-hidden="true" />
          )}
        </span>
        <div>
          <CardTitle>{alert.title}</CardTitle>
          <p className="mt-1 text-[14.5px] leading-relaxed text-ink-600">{alert.description}</p>
          {alert.source_label ? (
            <p data-testid={`apoio-alert-source-${alert.id}`} className="mt-1.5 text-[13px] font-semibold text-sage-700">
              Fonte: {alert.source_label}
            </p>
          ) : null}
          <CardMeta>
            <span className="mt-2 block">{formatDate(alert.created_at)}</span>
          </CardMeta>
        </div>
      </div>
      <StatusBadge read={alert.read} unreadLabel="Novo" testId={`apoio-alert-read-${alert.id}`} />
    </div>
    {!alert.read && onMarkRead ? (
      <ActionButton
        icon={Check}
        data-testid={`apoio-alert-mark-${alert.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onMarkRead(alert);
        }}
        className="mt-4 w-full sm:w-auto"
      >
        Marcar como lido
      </ActionButton>
    ) : null}
  </Card>
);

export default function ApoioAlerts() {
  const { api, forceSignOut } = useAuth();
  const navigate = useNavigate();
  const { selectedPatientId, selectedPatient, epoch, getSignal, loadPatients, handleAccessLost } = useSelectedPatient();
  const [alerts, setAlerts] = useState(null);

  const load = useCallback(async () => {
    if (!selectedPatientId) return;
    setAlerts(null);
    try {
      const { data } = await api.get("/network/alerts", {
        params: { patient_id: selectedPatientId },
        signal: getSignal(),
      });
      setAlerts(data);
    } catch (e) {
      if (e.code === "ERR_CANCELED" || e.name === "CanceledError") return;
      if (e.response?.status === 401) {
        forceSignOut();
        return;
      }
      if (e.response?.status === 403) {
        handleAccessLost();
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail));
      setAlerts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, forceSignOut, getSignal, handleAccessLost, selectedPatientId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId, epoch]);

  const markRead = async (alert) => {
    try {
      await api.post("/network/read-receipts", { entity_type: alert.type, entity_id: alert.id });
      toast.success("Alerta marcado como lido");
      await load();
      await loadPatients();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (alerts === null) return <Loading label="Carregando alertas…" />;

  const clinical = alerts.filter((a) => (a.scope || "clinical") === "clinical");
  const general = alerts.filter((a) => (a.scope || "clinical") !== "clinical");

  return (
    <div data-testid="apoio-alerts-page" className="space-y-8">
      <PageHero
        testId="apoio-alerts-hero"
        kicker="Novidades autorizadas"
        title="Alertas"
        subtitle={`Acompanhe com calma o que mudou no cuidado de ${selectedPatient?.patient_name || "seu paciente"}.`}
        banner="alertas"
      />

      <p data-testid="apoio-alerts-patient" className="sr-only">
        {selectedPatient?.patient_name}
      </p>

      <PushPreferenceCard
        app="apoio"
        testId="apoio-push-preference"
        title="Avisos no celular"
        description="Receba um aviso quando houver um novo boletim. O aviso nunca mostra dados do paciente."
      />

      <Section label="Avisos do cuidado" hint={clinical.some((a) => !a.read) ? "há novidades" : "tudo lido"}>
        {clinical.length === 0 ? (
          <StateBlock
            testId="apoio-alerts-empty"
            icon={Bell}
            title="Nada novo por aqui"
            description="Você verá avisos quando houver boletim publicado ou alteração nos medicamentos compartilhados deste paciente."
          />
        ) : (
          clinical.map((a, i) => (
            <AlertCard
              key={`${a.type}-${a.id}`}
              alert={a}
              index={i}
              onOpen={(alert) => navigate(alert.type === "bulletin" ? "/apoio/boletins" : "/apoio/medicamentos")}
              onMarkRead={markRead}
            />
          ))
        )}
      </Section>

      {general.length ? (
        <Section label="Conta e segurança" testId="apoio-alerts-general" className="border-t border-border pt-7">
          {general.map((a, i) => (
            <AlertCard key={`${a.type}-${a.id}`} alert={a} index={i} />
          ))}
        </Section>
      ) : null}
    </div>
  );
}
